import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type {
  WhatsAppApprovedTemplate,
  WhatsAppCompanyGroup,
  WhatsAppDirectoryContact,
} from '../../types/whatsapp';
import { WHATSAPP_NO_COMPANY_ID, WHATSAPP_NO_COMPANY_LABEL } from '../../types/whatsapp';

interface NewOutboundConversationDialogProps {
  open: boolean;
  companies: WhatsAppCompanyGroup[];
  contacts: WhatsAppDirectoryContact[];
  templates: WhatsAppApprovedTemplate[];
  submitting?: boolean;
  onClose: () => void;
  onStart: (contact: WhatsAppDirectoryContact, template: WhatsAppApprovedTemplate) => void | Promise<void>;
}

const NewOutboundConversationDialog: React.FC<NewOutboundConversationDialogProps> = ({
  open,
  companies,
  contacts,
  templates,
  submitting = false,
  onClose,
  onStart,
}) => {
  const [groupId, setGroupId] = useState<string>(WHATSAPP_NO_COMPANY_ID);
  const [selectedContactId, setSelectedContactId] = useState<string>('');
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [contactSearch, setContactSearch] = useState('');
  const [error, setError] = useState('');

  const approvedTemplates = useMemo(
    () => templates.filter((t) => t.status === 'APPROVED'),
    [templates]
  );

  const selectedTemplate = approvedTemplates.find((t) => t.id === selectedTemplateId) ?? null;

  const reset = () => {
    setGroupId(WHATSAPP_NO_COMPANY_ID);
    setSelectedContactId('');
    setSelectedTemplateId('');
    setContactSearch('');
    setError('');
  };

  useEffect(() => {
    if (open) reset();
  }, [open]);

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const filteredContacts = useMemo(() => {
    const q = contactSearch.trim().toLowerCase();
    return contacts.filter((c) => {
      if (!c.active) return false;
      if (groupId === WHATSAPP_NO_COMPANY_ID) {
        if (c.companyId != null) return false;
      } else if (c.companyId !== groupId) {
        return false;
      }
      if (!q) return true;
      const hay = `${c.displayName ?? ''} ${c.phone}`.toLowerCase();
      return hay.includes(q);
    });
  }, [contacts, groupId, contactSearch]);

  const handleStart = () => {
    setError('');
    const contact = contacts.find((c) => c.id === selectedContactId);
    if (!contact) {
      setError('Selecciona un contacto de la lista.');
      return;
    }
    if (!selectedTemplate) {
      setError('Selecciona un mensaje inicial.');
      return;
    }
    void onStart(contact, selectedTemplate);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Nueva conversación</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            Selecciona la empresa, el contacto y el mensaje para empezar.
          </Typography>

          <FormControl fullWidth size="small">
            <InputLabel>Empresa</InputLabel>
            <Select
              label="Empresa"
              value={groupId}
              onChange={(e) => {
                setGroupId(e.target.value);
                setSelectedContactId('');
              }}
            >
              <MenuItem value={WHATSAPP_NO_COMPANY_ID}>{WHATSAPP_NO_COMPANY_LABEL}</MenuItem>
              {companies.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            fullWidth
            label="Buscar contacto"
            value={contactSearch}
            onChange={(e) => setContactSearch(e.target.value)}
          />
          <Typography variant="caption" color="text.secondary">
            Contactos de{' '}
            <strong>
              {groupId === WHATSAPP_NO_COMPANY_ID
                ? WHATSAPP_NO_COMPANY_LABEL
                : companies.find((c) => c.id === groupId)?.name}
            </strong>
          </Typography>
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 180, overflow: 'auto' }}>
            <List dense disablePadding>
              {filteredContacts.map((c) => (
                <ListItemButton
                  key={c.id}
                  selected={selectedContactId === c.id}
                  onClick={() => setSelectedContactId(c.id)}
                >
                  <ListItemText
                    primary={c.displayName?.trim() || c.phone}
                    secondary={c.displayName ? c.phone : 'Sin nombre'}
                  />
                </ListItemButton>
              ))}
              {filteredContacts.length === 0 && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    No hay contactos en este agrupador.
                  </Typography>
                </Box>
              )}
            </List>
          </Box>

          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Mensaje inicial
          </Typography>
          <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 220, overflow: 'auto' }}>
            <List dense disablePadding>
              {approvedTemplates.map((t) => (
                <ListItemButton
                  key={t.id}
                  selected={selectedTemplateId === t.id}
                  onClick={() => setSelectedTemplateId(t.id)}
                  alignItems="flex-start"
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {t.name}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" component="span" display="block" sx={{ mt: 0.5 }}>
                        {t.body}
                      </Typography>
                    }
                  />
                </ListItemButton>
              ))}
              {approvedTemplates.length === 0 && (
                <Box sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    No hay mensajes disponibles.
                  </Typography>
                </Box>
              )}
            </List>
          </Box>

          {selectedTemplate && (
            <Alert severity="info">
              Se enviará: <strong>{selectedTemplate.name}</strong>
            </Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} sx={{ textTransform: 'none' }}>
          Cancelar
        </Button>
        <Button variant="contained" onClick={handleStart} disabled={submitting} sx={{ textTransform: 'none' }}>
          {submitting ? 'Enviando...' : 'Enviar mensaje'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewOutboundConversationDialog;
